import {
    useImperativeHandle,
    useState,
    forwardRef,
    useEffect,
} from 'react';
import { Spin } from 'antd';

export interface AppFlashScreenRef {
    hide: () => void;
}

interface AppFlashScreenProps {
    tip?: string;
    size?: 'small' | 'default' | 'large';
    fullscreen?: boolean;
    duration?: number; // thời gian fade-out (ms)
    hasLogo?: boolean; // hiển thị logo thay vì spinner
    logoSrc?: string; // URL của logo
    logoStyle?: React.CSSProperties; // custom style cho logo
}

const AppFlashScreen = forwardRef<AppFlashScreenRef, AppFlashScreenProps>(
    ({
        tip = 'Loading...',
        size = 'large',
        fullscreen = true,
        duration = 500,
        hasLogo = false,
        logoSrc = '/images/logo-main.png',
        logoStyle = {}
    }, ref) => {
        const [visible, setVisible] = useState(true);
        const [fadeOut, setFadeOut] = useState(false);

        // Tạo CSS animation cho logo pulse
        useEffect(() => {
            if (hasLogo) {
                const styleId = 'logo-pulse-animation';
                const existingStyle = document.getElementById(styleId);

                if (!existingStyle) {
                    const style = document.createElement('style');
                    style.id = styleId;
                    style.textContent = `
                        @keyframes logoPulse {
                            0% {
                                opacity: 0.8;
                                transform: scale(1);
                            }
                            50% {
                                opacity: 1;
                                transform: scale(1.05);
                            }
                            100% {
                                opacity: 0.8;
                                transform: scale(1);
                            }
                        }
                        .logo-pulse {
                            animation: logoPulse 2s ease-in-out infinite;
                        }
                    `;
                    document.head.appendChild(style);
                }
            }
        }, [hasLogo]);

        const hide = () => {
            setFadeOut(true);
            setTimeout(() => {
                setVisible(false);
            }, duration);
        };

        // expose `hide` to parent
        useImperativeHandle(ref, () => ({ hide }));

        if (!visible) return null;

        return (
            <div
                style={{
                    position: fullscreen ? 'fixed' : 'relative',
                    top: 0,
                    left: 0,
                    width: fullscreen ? '100vw' : '100%',
                    height: fullscreen ? '100vh' : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: fullscreen ? 'rgba(255,255,255,0.9)' : 'transparent',
                    zIndex: fullscreen ? 9999 : 'auto',
                    opacity: fadeOut ? 0 : 1,
                    visibility: fadeOut ? 'hidden' : 'visible',
                    transition: `opacity ${duration}ms ease-in-out, visibility ${duration}ms ease-in-out`,
                }}
            >
                <div style={{ textAlign: 'center' }}>
                    {hasLogo ? (
                        <>
                            <img
                                src={logoSrc}
                                alt="Loading Logo"
                                className="logo-pulse"
                                style={{
                                    maxWidth: '200px',
                                    maxHeight: '100px',
                                    objectFit: 'contain',
                                    ...logoStyle
                                }}
                                onError={(e) => {
                                    console.error('Logo loading failed:', logoSrc);
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                            {tip && (
                                <div style={{
                                    marginTop: '16px',
                                    color: '#666',
                                    fontSize: '14px'
                                }}>
                                    {tip}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <Spin size={size} spinning={true} />
                            {tip && (
                                <div style={{
                                    marginTop: '16px',
                                    color: '#666',
                                    fontSize: '14px'
                                }}>
                                    {tip}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        );

    }
);

export default AppFlashScreen;
